import type { FC, ReactNode } from 'react';

import { useAtomValue } from 'jotai';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import type { FilterKey } from '@core/Entities/Search/QueryParams.entity';
import {
  BODY_TYPES,
  type BodyTypeMessageId,
} from '@core/Utils/VehicleMappedMessages/BodyTypeMappings.constants';
import { MultiSelect } from '@plp/Components/MultiSelect/MultiSelect';
import { getOptionsWithAggs } from '@plp/Utils/Filters/GetOptionsWithAggs';
import { useFilterLink } from '@search-filters/Hooks/UseFilterLink/UseFilterLink';
import {
  type MultiSelectOption,
  useMultiSelectFilters,
} from '@search-filters/Hooks/UseMultiSelectFilters';

import type { FilterControlBaseProps, PlpFiltersMessagesKeys } from '../../FilterPanel.entity';
import { FilterLink } from '../FilterLink/FilterLink';
import { ICON_MAPPER } from './BodyTypeFilter.constants';

import styles from './BodyTypeFilter.module.css';

type BodyTypeFilterProps = FilterControlBaseProps;
const filterId: FilterKey = 'body-type';

const BodyTypeFilter: FC<BodyTypeFilterProps> = ({ strings, isPlpFiltersSeoEnabled, tenant }) => {
  const aggs = useAtomValue(aggregationsAtom);
  const { getLinkForFilterValue } = useFilterLink({
    filterId,
    isPlpFiltersSeoEnabled,
    tenant,
  });
  const listEnhanceFn = (bodyType: MultiSelectOption, selected: string[], noOfOptions: number) => {
    const isOnlyOption = noOfOptions === 1;
    const link = getLinkForFilterValue({
      filterValue: bodyType.id,
      resultCount: bodyType.count,
      isOnlyOption,
    });

    return {
      ...bodyType,
      renderLabel: renderLabel(
        bodyType.labelKey as BodyTypeMessageId,
        <FilterLink link={link}>{strings[bodyType.labelKey as PlpFiltersMessagesKeys]}</FilterLink>,
      ),
      selected: selected?.includes(bodyType.id) || isOnlyOption,
      ...(isOnlyOption ? { disabled: true } : {}),
    };
  };

  const { handleSelect, listWithSelected } = useMultiSelectFilters(
    filterId,
    getOptionsWithAggs(BODY_TYPES, aggs['body-type']),
    strings,
    isPlpFiltersSeoEnabled,
    tenant,
    listEnhanceFn,
  );

  return (
    <section className={styles.bodyTypeFilter}>
      <MultiSelect
        groupName="body-type"
        classes={{ label: styles.listItem }}
        items={listWithSelected}
        onSelect={handleSelect}
        dataTestId="body-type-filter-list"
      />
    </section>
  );
};

const renderLabel = (name: BodyTypeMessageId, label: ReactNode) => {
  const Component = ICON_MAPPER[name];
  return Component ? (
    <>
      <Component
        fontSize={40}
        className={name === 'motorhome' || name === 'sportscar' ? '' : styles.icon}
      />{' '}
      {label}
    </>
  ) : null;
};

export default BodyTypeFilter;
